import {
  CLOSING_OUTCOME_LABELS,
  type ClosingProblem,
  type HandoverDocument,
  SIGNER_KIND_LABELS,
  handoverDocumentSchema,
} from '@sivan/shared';
import { useId, useState } from 'react';
import { ApiError, NetworkError, apiPost } from '../api/client';
import { askConfirm, askText } from '../ui/confirm';
import { Icon } from '../ui/Icon';
import { SignaturePad } from './SignaturePad';

const PROBLEM_TEXT: Readonly<Record<ClosingProblem, (count: number | null) => string>> = {
  acknowledgment_missing: () =>
    'נוסח ההצהרה של הארגון עדיין לא הוזן. מנהל־על מזין אותו בהגדרות המסירות; עד אז אי אפשר לסגור מסירות.',
  inspection_not_started: () => 'בדיקת הדירה לפי התבנית לא התחילה.',
  items_unchecked: (n) =>
    n === 1 ? 'פריט בדיקה אחד עדיין לא נבדק.' : `${String(n)} פריטי בדיקה עדיין לא נבדקו.`,
  general_checks_unchecked: (n) =>
    n === 1 ? 'בדיקה כללית אחת עדיין לא סומנה.' : `${String(n)} בדיקות כלליות עדיין לא סומנו.`,
  reinspections_pending: (n) =>
    n === 1
      ? 'ליקוי אחד מהמסירה הראשונית עדיין לא נבדק מחדש.'
      : `${String(n)} ליקויים מהמסירה הראשונית עדיין לא נבדקו מחדש.`,
  findings_without_photo: (n) =>
    n === 1 ? 'לליקוי אחד אין תמונה.' : `ל-${String(n)} ליקויים אין תמונה.`,
  recipient_signature_missing: () => 'חסרה חתימת המקבל (רוכש או מיופה כוח).',
  company_signature_missing: () => 'חסרה חתימת נציג החברה.',
};

function explain(error: unknown): string {
  if (error instanceof NetworkError) return 'אין חיבור לשרת. לא נשמר דבר; נסו שוב כשיהיה חיבור.';
  if (error instanceof ApiError) {
    if (error.status === 409)
      return 'המסירה השתנתה בינתיים או שכבר אינה טיוטה. רעננו אותה לפני החתימה.';
    if (error.status === 403) return 'אין לך הרשאה לחתום או לסגור מסירה בפרויקט הזה.';
    if (error.status === 422) return 'המסירה עדיין לא מוכנה לסגירה. בדקו את הרשימה למעלה.';
  }
  return 'הפעולה נכשלה. לא נשמר דבר.';
}

/** Sharing a file (the phone's share sheet) where the browser supports it. */
function canShareFiles(): boolean {
  if (typeof navigator.canShare !== 'function') return false;
  try {
    return navigator.canShare({
      files: [new File([''], 'protocol.pdf', { type: 'application/pdf' })],
    });
  } catch {
    return false;
  }
}

/** Fetches the protocol (authorized like any request) and opens the share sheet with it. */
async function shareProtocol(url: string, protocolNumber: string): Promise<void> {
  const response = await fetch(url, { credentials: 'same-origin' });
  if (!response.ok) throw new Error('protocol unavailable');
  const file = new File([await response.blob()], `protocol-${protocolNumber}.pdf`, {
    type: 'application/pdf',
  });
  await navigator.share({ files: [file], title: `פרוטוקול ${protocolNumber}` });
}

const when = (iso: string) =>
  new Date(iso).toLocaleString('he-IL', {
    timeZone: 'Asia/Jerusalem',
    dateStyle: 'short',
    timeStyle: 'short',
  });

/**
 * Signing and closing a handover. The server decides (the same rules everywhere): the list of
 * what is missing comes from it, and the buttons only offer what it will accept. Nothing here is
 * stored in the browser; a signature is sent once, for the version shown, and any later change to
 * the draft removes it.
 */
export function ClosingPanel({
  doc,
  dirty,
  onChanged,
  protocolUrl,
  actions = {},
}: {
  readonly doc: HandoverDocument;
  /** Unsaved changes: nothing is signed or closed until the draft is saved. */
  readonly dirty: boolean;
  readonly onChanged: (doc: HandoverDocument) => void;
  /** The protocol PDF of a closed handover (null: not available here). */
  readonly protocolUrl: string | null;
  /** Where to go to fix a problem (the item, the check, the finding without a photo). */
  readonly actions?: Partial<Record<ClosingProblem, { label: string; run: () => void }>>;
}) {
  const id = useId();
  const { closing } = doc;
  const [kind, setKind] = useState<'purchaser' | 'representative'>('purchaser');
  const [name, setName] = useState('');
  const [reference, setReference] = useState('');
  const [recipientPng, setRecipientPng] = useState<string | null>(null);
  const [companyPng, setCompanyPng] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const online = typeof navigator === 'undefined' || navigator.onLine;

  if (closing.closed !== null)
    return (
      <section className={`closing closing--${closing.closed.outcome}`} aria-label="סגירת המסירה">
        <h2 className="section-title">{CLOSING_OUTCOME_LABELS[closing.closed.outcome]}</h2>
        <p>
          נסגרה ב-{when(closing.closed.at)} (שעון ישראל) על ידי {closing.closed.by}.
          {closing.closed.refusalReason !== null && (
            <> סיבת הסירוב: {closing.closed.refusalReason}</>
          )}
        </p>
        {closing.recipientSignature !== null && (
          <p className="hint">
            חתם/ה: {closing.recipientSignature.signerName} (
            {SIGNER_KIND_LABELS[closing.recipientSignature.signerKind]})
          </p>
        )}
        <p className="hint">המסירה סגורה ואינה ניתנת לשינוי.</p>
        {protocolUrl !== null && (
          <div className="closing__actions">
            <a className="button button--primary" href={protocolUrl} download>
              <Icon name="file" /> הורדת הפרוטוקול (PDF)
            </a>
            {canShareFiles() && (
              <button
                type="button"
                className="button"
                onClick={() => {
                  void shareProtocol(protocolUrl, doc.protocolNumber).catch(() => undefined);
                }}
              >
                שיתוף
              </button>
            )}
          </div>
        )}
      </section>
    );
  if (!doc.permissions.close) return null;

  const run = (action: () => Promise<HandoverDocument>) => {
    setBusy(true);
    setError(null);
    action()
      .then(onChanged)
      .catch((e: unknown) => {
        setError(explain(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const path = `/projects/${doc.project.id}/handovers/${doc.id}`;
  const signRecipient = () => {
    if (recipientPng === null) return;
    run(() =>
      apiPost(
        `${path}/signatures`,
        {
          rowVersion: doc.rowVersion,
          role: 'recipient',
          signerKind: kind,
          signerName: name.trim(),
          ...(kind === 'representative' ? { representativeReference: reference.trim() } : {}),
          image: recipientPng,
        },
        handoverDocumentSchema,
      ),
    );
  };
  const signCompany = () => {
    if (companyPng === null) return;
    run(() =>
      apiPost(
        `${path}/signatures`,
        {
          rowVersion: doc.rowVersion,
          role: 'company',
          signerKind: 'company',
          signerName: 'נציג החברה',
          image: companyPng,
        },
        handoverDocumentSchema,
      ),
    );
  };
  const complete = () => {
    void askConfirm({
      title: 'לסיים את המסירה?',
      message:
        'המסירה תיסגר כ"הושלמה" ולא ניתן יהיה לשנות אותה. ייווצר פרוטוקול PDF חתום מהמצב הנוכחי.',
      confirmLabel: 'סיום המסירה',
    }).then((yes) => {
      if (yes)
        run(() =>
          apiPost(
            `${path}/close`,
            { rowVersion: doc.rowVersion, outcome: 'completed' },
            handoverDocumentSchema,
          ),
        );
    });
  };
  const refuse = () => {
    void askText({
      title: 'רישום סירוב לחתום',
      message:
        'המסירה תיסגר כ"לא הושלמה – סירוב לחתימה", ולא תיחשב כמסירה שהושלמה. הרישום אינו ניתן לשינוי.',
      label: 'סיבת הסירוב, כפי שנמסרה',
      confirmLabel: 'רישום הסירוב',
      danger: true,
      maxLength: 500,
    }).then((reason) => {
      if (reason === null || reason.trim().length < 3) return;
      run(() =>
        apiPost(
          `${path}/close`,
          { rowVersion: doc.rowVersion, outcome: 'refused', refusalReason: reason.trim() },
          handoverDocumentSchema,
        ),
      );
    });
  };

  const recipientReady =
    recipientPng !== null &&
    name.trim().length >= 2 &&
    (kind === 'purchaser' || reference.trim().length >= 2);
  const blocked = dirty || busy || !online;
  const problems = closing.problems;
  const signaturesOnly = problems.every(
    (p) => p.code === 'recipient_signature_missing' || p.code === 'company_signature_missing',
  );
  // The receiving party signs the finished protocol; the company may sign earlier (a refusal
  // needs the company's signature only).
  const canSign = closing.acknowledgment !== null;

  return (
    <section className="closing" aria-labelledby={`${id}-title`}>
      <h2 className="section-title" id={`${id}-title`}>
        חתימה וסיום המסירה
      </h2>
      {problems.length === 0 ? (
        <p className="notice notice--success" role="status">
          <Icon name="check" /> הכול מוכן: אפשר לסיים את המסירה.
        </p>
      ) : (
        <ul className="closing__problems" aria-label="מה חסר לסיום">
          {problems.map((p) => {
            const action = actions[p.code];
            return (
              <li key={p.code}>
                <Icon name="alert" />
                <span>{PROBLEM_TEXT[p.code](p.count)}</span>
                {action !== undefined && (
                  <button type="button" className="button button--small" onClick={action.run}>
                    {action.label}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {dirty && (
        <p className="notice notice--warning" role="status">
          יש שינויים שעדיין נשמרים. החתימה והסיום זמינים אחרי השמירה.
        </p>
      )}
      {!online && (
        <p className="notice notice--warning" role="status">
          <Icon name="cloud-off" /> חתימה וסיום דורשים חיבור.
        </p>
      )}
      {error !== null && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}

      {closing.acknowledgment !== null && (
        <figure className="closing__acknowledgment">
          <figcaption>נוסח ההצהרה (גרסה {closing.acknowledgment.version})</figcaption>
          <blockquote>{closing.acknowledgment.text}</blockquote>
        </figure>
      )}

      {canSign && (
        <div className="closing__signatures">
          <fieldset className="closing__party">
            <legend>חתימת המקבל</legend>
            {!signaturesOnly ? (
              <p className="hint">חתימת המקבל תתאפשר אחרי שכל מה שברשימה למעלה יושלם.</p>
            ) : closing.recipientSignature !== null ? (
              <p className="closing__signed">
                <Icon name="check" /> נחתם על ידי {closing.recipientSignature.signerName} (
                {SIGNER_KIND_LABELS[closing.recipientSignature.signerKind]})
              </p>
            ) : (
              <>
                <div className="closing__kinds" role="radiogroup" aria-label="מי חותם">
                  {(['purchaser', 'representative'] as const).map((k) => (
                    <label key={k} className="choice">
                      <input
                        type="radio"
                        name={`${id}-kind`}
                        checked={kind === k}
                        onChange={() => {
                          setKind(k);
                        }}
                      />
                      <span>{SIGNER_KIND_LABELS[k]}</span>
                    </label>
                  ))}
                </div>
                <label className="field">
                  <span>שם החותם/ת</span>
                  <input
                    value={name}
                    maxLength={120}
                    autoComplete="off"
                    onChange={(e) => {
                      setName(e.target.value);
                    }}
                  />
                </label>
                {kind === 'representative' && (
                  <label className="field">
                    <span>פרטי ייפוי הכוח (מספר, תאריך או עורך הדין)</span>
                    <input
                      value={reference}
                      maxLength={200}
                      autoComplete="off"
                      onChange={(e) => {
                        setReference(e.target.value);
                      }}
                    />
                  </label>
                )}
                <SignaturePad label="חתימת המקבל" onChange={setRecipientPng} />
                <button
                  type="button"
                  className="button button--secondary"
                  disabled={!recipientReady || blocked}
                  onClick={signRecipient}
                >
                  שמירת חתימת המקבל
                </button>
              </>
            )}
          </fieldset>

          <fieldset className="closing__party">
            <legend>חתימת נציג החברה</legend>
            {closing.companySignature !== null ? (
              <p className="closing__signed">
                <Icon name="check" /> נחתם על ידי {closing.companySignature.signerName}
              </p>
            ) : (
              <>
                <p className="hint">החתימה נרשמת בשם המשתמש המחובר.</p>
                <SignaturePad label="חתימת נציג החברה" onChange={setCompanyPng} />
                <button
                  type="button"
                  className="button button--secondary"
                  disabled={companyPng === null || blocked}
                  onClick={signCompany}
                >
                  שמירת חתימת החברה
                </button>
              </>
            )}
          </fieldset>
        </div>
      )}

      <div className="closing__actions">
        <button
          type="button"
          className="button button--primary"
          disabled={problems.length > 0 || blocked}
          onClick={complete}
        >
          <Icon name="check" /> סיום המסירה
        </button>
        {closing.acknowledgment !== null && closing.recipientSignature === null && (
          <button
            type="button"
            className="button"
            disabled={blocked || closing.companySignature === null}
            title={closing.companySignature === null ? 'נדרשת קודם חתימת נציג החברה' : undefined}
            onClick={refuse}
          >
            רישום סירוב לחתום
          </button>
        )}
      </div>
      <p className="hint">
        שינוי במסירה אחרי החתימה מבטל את החתימות, וצריך לחתום שוב. המסמך אינו מהווה חוות דעת משפטית
        לגבי תוקף החתימה.
      </p>
    </section>
  );
}
